import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import TagChip from '../TagChip/TagChip'
import TagInput from '../TagInput/TagInput'
import styles from './TagCellEditor.module.scss'

export interface TagCellEditorProps {
  tags: string[]
  onSave: (next: string[]) => void | Promise<unknown>
  disabled?: boolean
  compact?: boolean
  suggestions?: string[]
}

const POPOVER_WIDTH = 280
const POPOVER_MIN_HEIGHT = 120
const VIEWPORT_MARGIN = 8
const GAP = 4

interface PopoverPos {
  top: number
  left: number
  width: number
}

export default function TagCellEditor({
  tags,
  onSave,
  disabled,
  compact = true,
  suggestions,
}: TagCellEditorProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(tags)
  const [saving, setSaving] = useState(false)
  const [pos, setPos] = useState<PopoverPos | null>(null)
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) setDraft(tags)
  }, [tags, open])

  const isDirty = JSON.stringify(draft) !== JSON.stringify(tags)

  const handleSave = async () => {
    if (!isDirty) {
      setOpen(false)
      return
    }
    setSaving(true)
    try {
      await onSave(draft)
      setOpen(false)
    } catch {
      setDraft(tags)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setDraft(tags)
    setOpen(false)
  }

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return
    const computePosition = () => {
      const rect = anchorRef.current!.getBoundingClientRect()
      const maxWidth = Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2)
      let left = rect.left
      if (left + maxWidth > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, window.innerWidth - maxWidth - VIEWPORT_MARGIN)
      }
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN

      let top = rect.bottom + GAP
      if (top + POPOVER_MIN_HEIGHT > window.innerHeight - VIEWPORT_MARGIN) {
        const flipped = rect.top - POPOVER_MIN_HEIGHT - GAP
        top = flipped >= VIEWPORT_MARGIN ? flipped : VIEWPORT_MARGIN
      }
      setPos({ top, left, width: maxWidth })
    }
    computePosition()
    window.addEventListener('resize', computePosition)
    window.addEventListener('scroll', computePosition, true)
    return () => {
      window.removeEventListener('resize', computePosition)
      window.removeEventListener('scroll', computePosition, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      if (isDirty && !saving) {
        void handleSave()
      } else {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDirty, saving])

  const openEditor = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled) return
    e?.stopPropagation()
    setOpen(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openEditor(e)
    }
  }

  return (
    <div className={styles.cellRoot} onClick={(e) => e.stopPropagation()}>
      <div
        ref={anchorRef}
        className={`${styles.displayRow} ${disabled ? styles.disabled : ''}`}
        onClick={openEditor}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {tags.length === 0 ? (
          <span className={styles.placeholder}>+ tag</span>
        ) : (
          tags.map((t) => <TagChip key={t} label={t} size={compact ? 'sm' : 'md'} />)
        )}
      </div>

      {open && !disabled && pos && (
        <div
          className={styles.popover}
          ref={popoverRef}
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          <TagInput value={draft} onChange={setDraft} autoFocus suggestions={suggestions} />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={handleCancel}
              disabled={saving}
            >
              Annuler
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
